<template lang="pug">
div(:class="$vuetify.breakpoint.mobile ? 'pa-0' : 'pa-8'")
  div(:class="$vuetify.breakpoint.mobile ? 'd-flex align-center flex-nowrap overflow-x-auto gap-2 mb-4' : ''")
    range-dropdown-mobile(v-if="dates && $vuetify.breakpoint.mobile")
    div.d-flex.align-center.gap-2(:class="$vuetify.breakpoint.mobile ? '' : 'mb-6'")
      template(v-if="!$vuetify.breakpoint.mobile")
        div.text-3xl.font-semibold Payments
        v-spacer
      company-dropdown(v-if="isHq")
      provider-dropdown.mr-2
      range-dropdown(v-if="dates && !$vuetify.breakpoint.mobile")

  template(v-if="dates")
    v-row.mb-4
      v-col(cols="6")
        tri-stat-card(:loading="isFetchingStats" title="Panels" titleFirst="Patients" :valueFirst="panelStats.leads || 0" titleSecond="Total sales" :valueSecond="(panelStats.total_sales || 0) | displayPriceNoCent" titleThird="Average billing" :valueThird="(panelStats.avg_billing || 0) | displayPriceNoCent")
      v-col(cols="6")
        tri-stat-card(:loading="isFetchingStats" title="Self pay" titleFirst="Patients" :valueFirst="selfPayStats.leads || 0" titleSecond="Total sales" :valueSecond="(selfPayStats.total_sales || 0) | displayPriceNoCent" titleThird="Average billing" :valueThird="(selfPayStats.avg_billing || 0) | displayPriceNoCent")

    v-row
      v-col(cols="12")
        ranking-card(v-if="beginDate" type="panel" :providers="selectedProviders" :begin="beginDate" :until="untilDate")
      v-col(cols="12" sm="6")
        horizontal-bar-card(title="Payment method" datatype="currency" :loading="isLoadingPaymentMethods" :data="paymentMethodData")
      v-col(cols="12" sm="6")
        horizontal-bar-card(title="Price tier" datatype="currency" :loading="isLoadingPriceTiers" :data="priceTierData")
      v-col(cols="12" sm="4")
        stat-card(title="Total outstanding invoice" :value="totalOutstandingInvoice | displayPriceNoCent" :loading="isLoadingInvoices")
      v-col(cols="12" sm="4")
        stat-card(title="Numbers outstanding invoice" :value="numbersOutstandingInvoice" :loading="isLoadingInvoices")
      v-col(cols="12" sm="4")
        stat-card(title="Average days to paid" :value="averageDaysToPaid" :loading="isLoadingInvoices")
      v-col(cols="12" sm="6")
        ranking-card(v-if="beginDate" type="outstanding" :providers="selectedProviders" :begin="beginDate" :until="untilDate")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'
import RangeDropdown from './RangeDropdown.vue'
import RangeDropdownMobile from './RangeDropdownMobile.vue'
import ProviderDropdown from './ProviderDropdown.vue'
import TriStatCard from './TriStatCard.vue'
import HorizontalBarCard from './HorizontalBarCard.vue'
import RankingCard from './RankingCard.vue'
import StatCard from './StatCard.vue'
import CompanyDropdown from '@/components/hq/custom/CompanyDropdown.vue'

export default {
  name: 'InsightPayments',
  components: { RangeDropdown, RangeDropdownMobile, ProviderDropdown, TriStatCard, HorizontalBarCard, RankingCard, StatCard, CompanyDropdown },
  data: () => ({
    isFetchingStats: false,
    isLoadingPaymentMethods: true,
    isLoadingPriceTiers: true,
    isLoadingInvoices: true,
    paymentMethodData: null,
    priceTierData: null,
    totalOutstandingInvoice: 0,
    numbersOutstandingInvoice: 0,
    averageDaysToPaid: 0,
    panelStats: {},
    selfPayStats: {},
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany',
      dates: 'insight/getDates',
      beginDate: 'insight/getBeginDate',
      untilDate: 'insight/getUntilDate',
      selectedProviders: 'insight/getSelectedProviders',
      selectedCompanies: 'insight/getSelectedCompanies',
      currentDateRange: 'insight/getCurrentDateRange',
      isHq: 'hq/getIsHq'
    }),

  },
  watch: {
    dates () {
      this.fetchData()
    },
    selectedProviders: {
      handler() {
        this.fetchData()
      },
      deep: true
    },
    selectedCompanies: {
      handler() {
        this.fetchData()
      },
      deep: true
    }
  },
  created () {
    this.setupDates()
  },
  methods: {
    ...mapActions(['showMessage']),
    setupDates () {
      if (this.dates) {
        this.fetchData()
        return
      }
      const now = DateTime.local()
      const dateFormat = 'yyyy-MM-dd'
      const start = now.minus({ days: 6 })
      this.$store.commit('insight/setDates', [start.toFormat(dateFormat), now.toFormat(dateFormat)])
    },
    fetchData () {
      this.fetchStats()
      this.fetchPaymentMethods()
      this.fetchPriceTiers()
      this.fetchInvoiceData()
    },
    fetchStats (stopRefetch) {
      this.isFetchingStats = true
      const params = {
        form_type: 'registration',
        begin: this.beginDate,
        until: this.untilDate,
      }

      // Handle HQ vs regular company logic
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }

      if (this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/rankings/${this.company.slug}/payments`, { params })
        .then(resp => {
          const data = resp.data
          this.panelStats = data.panel
          this.selfPayStats = data.self_pay
          this.isFetchingStats = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchStats(true)
          } else {
            this.isFetchingStats = false
          }
        })
    },
    fetchPaymentMethods (stopRefetch) {
      this.isLoadingPaymentMethods = true
      const params = {
        form_type: 'registration',
        begin: this.beginDate,
        until: this.untilDate,
      }

      // Handle HQ vs regular company logic
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }

      if (this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/rankings/${this.company.slug}/payments/methods`, { params })
        .then(resp => {
          const data = resp.data
          const obj = {
            labels: [],
            values: [],
            percentage: []
          }
          Object.keys(data).forEach(key => {
            obj.labels.push(key)
            obj.values.push(data[key].sales)
            obj.percentage.push(data[key].pct)
          })
          this.paymentMethodData = obj
          this.isLoadingPaymentMethods = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchPaymentMethods(true)
          } else {
            this.isLoadingPaymentMethods = false
          }
        })
    },
    fetchPriceTiers (stopRefetch) {
      this.isLoadingPriceTiers = true
      const params = {
        form_type: 'registration',
        begin: this.beginDate,
        until: this.untilDate,
      }

      // Handle HQ vs regular company logic
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }

      if (this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/rankings/${this.company.slug}/sales/price_tiers`, { params })
        .then(resp => {
          const data = resp.data
          const obj = {
            labels: [],
            values: [],
            percentage: [],
          }
          Object.keys(data).forEach(key => {
            obj.labels.push(key)
            obj.values.push(data[key].sales)
            obj.percentage.push(data[key].pct)
          })
          this.priceTierData = obj
          this.isLoadingPriceTiers = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchPriceTiers(true)
          } else {
            this.isLoadingPriceTiers = false
          }
        })
    },
    fetchInvoiceData (stopRefetch) {
      this.isLoadingInvoices = true
      let params = {
        form_type: 'registration',
        begin: this.beginDate,
        until: this.untilDate,
      }

      // Handle HQ vs regular company logic
      if (this.isHq) {
        if (this.selectedCompanies) {
          params.companies = this.selectedCompanies.map(c => c.id).join(',')
        } else {
          params.hq = this.company.id
        }
      } else {
        params.company = this.company.id
      }

      if (this.selectedProviders && this.selectedProviders.length > 0) {
        params.providers = this.selectedProviders.map(p => p.id).join(',')
      }
      this.$axios.get(`/rankings/${this.company.slug}/invoices/stats`, { params })
        .then(resp => {
          this.totalOutstandingInvoice = resp.data.unpaid_amount
          this.numbersOutstandingInvoice = resp.data.unpaid_count
          this.averageDaysToPaid = resp.data.avg_days_to_paid
          this.isLoadingInvoices = false
        })
        .catch(() => {
          if (!stopRefetch) {
            this.fetchInvoiceData(true)
          } else {
            this.isLoadingInvoices = false
          }
        })
    },
  }
}
</script>